import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";
import { X, Plus, GripVertical, CheckCircle2, AlertCircle } from "lucide-react";

const STORY_KEYS = [
  { key: "wedding", label: "Wedding" },
  { key: "elopement", label: "Elopement" },
  { key: "family", label: "Family" },
  { key: "proposal", label: "Proposal" },
  { key: "engagement", label: "Engagement" },
  { key: "love_story", label: "Love Story" },
  { key: "styled_shoot", label: "Styled Shoot (all questions asked)" },
];

export default function Settings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newCat, setNewCat] = useState("");
  const [storyLib, setStoryLib] = useState({}); // {catKey: [{q, always}]}
  const [ghlResult, setGhlResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [r, lib] = await Promise.all([
          api.get("/admin/settings"),
          api.get("/admin/story-library"),
        ]);
        setS(r.data);
        const map = {};
        Object.entries(lib.data || {}).forEach(([k, v]) => {
          map[k] = (v.questions || []).map((x) => ({ q: x.q, always: !!x.always }));
        });
        setStoryLib(map);
        setLoadError("");
      } catch {
        setLoadError("Could not load settings. Please sign in again or refresh the page.");
      }
    })();
  }, []);

  const set = (k, v) => setS((cur) => ({ ...cur, [k]: v }));

  // GHL custom field box: accepts the GHL Unique Key (resolved on save) or a raw ID;
  // shows the matched field name (or a clear error) next to it.
  const CfField = ({ pub, f, label, testid }) => {
    const base = `ghl_${pub}_cf_${f}`;
    const name = s?.[`${base}_name`];
    const err = s?.[`${base}_error`];
    const resolved = s?.[`${base}_id`];
    return (
      <div>
        <Field label={label} testid={testid} value={s?.[base] || ""} onChange={(v) => set(base, v)} />
        {err ? (
          <p className="text-xs text-[#b35c5c] -mt-1" data-testid={`${testid}-status`}>⚠ {err}</p>
        ) : name ? (
          <p className="text-xs text-[#768c6a] -mt-1" data-testid={`${testid}-status`}>Matched: {name}</p>
        ) : resolved ? (
          <p className="text-xs text-[#5c5852] -mt-1" data-testid={`${testid}-status`}>Raw ID saved</p>
        ) : null}
      </div>
    );
  };

  const save = async () => {
    setSaving(true);
    try {
      const story_questions = {};
      Object.entries(storyLib).forEach(([k, arr]) => {
        const items = (arr || []).filter((x) => (x.q || "").trim()).map((x) => ({
          q: x.q.trim(), always: !!x.always,
        }));
        if (items.length > 0) story_questions[k] = items;
      });
      const payload = { ...s, story_questions };
      const { data } = await api.patch("/admin/settings", payload);
      setS(data);
      toast.success("Saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const updateQuestion = (catKey, idx, patch) =>
    setStoryLib((lib) => ({
      ...lib,
      [catKey]: (lib[catKey] || []).map((it, i) => (i === idx ? { ...it, ...patch } : it)),
    }));

  const addQuestion = (catKey) =>
    setStoryLib((lib) => ({
      ...lib,
      [catKey]: [...(lib[catKey] || []), { q: "", always: false }],
    }));

  const removeQuestion = (catKey, idx) =>
    setStoryLib((lib) => ({
      ...lib,
      [catKey]: (lib[catKey] || []).filter((_, i) => i !== idx),
    }));

  const [wpRefreshing, setWpRefreshing] = useState(false);
  const [wpResult, setWpResult] = useState(null);
  const [driveTesting, setDriveTesting] = useState(false);
  const [driveResult, setDriveResult] = useState(null);
  const [resendTestTo, setResendTestTo] = useState({ dwh: "", dwt: "" });
  const [resendTesting, setResendTesting] = useState("");

  const testResend = async (pub) => {
    if (!(resendTestTo[pub] || "").includes("@")) {
      toast.error("Enter an email address to send the test to");
      return;
    }
    setResendTesting(pub);
    try {
      const { data } = await api.post("/admin/settings/resend/test", {
        publication: pub, to: resendTestTo[pub].trim() });
      if (data.ok) toast.success(data.message);
      else toast.warning(data.message);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Test email failed");
    } finally {
      setResendTesting("");
    }
  };

  const testDrive = async () => {
    setDriveTesting(true);
    setDriveResult(null);
    try {
      const { data } = await api.post("/admin/drive/test");
      setDriveResult(data);
      if (data.ok) toast.success("Both Blogs folders reachable");
      else toast.error("Drive access test failed");
    } catch (e) {
      setDriveResult({ message: e.message });
      toast.error("Drive access test failed");
    } finally {
      setDriveTesting(false);
    }
  };

  const refreshWp = async () => {
    setWpRefreshing(true);
    setWpResult(null);
    try {
      // Persist the URLs exactly as typed first, so the refresh uses them
      // and a later Save can never wipe them.
      await api.patch("/admin/settings", {
        wp_dwh_url: s.wp_dwh_url || "",
        wp_dwt_url: s.wp_dwt_url || "",
      });
      const { data } = await api.post("/admin/wp/refresh");
      const skipped = data.skipped || [];
      // Update ONLY the WordPress status lines — never replace other
      // unsaved form fields with the server copy.
      setS((cur) => ({ ...cur, wp_cache: data.wp_cache || {} }));
      setWpResult(skipped.length === 2 ? null : (data.wp_cache || {}));
      if (skipped.length === 2) {
        toast.error("Add a WordPress site URL first, then refresh");
      } else if (skipped.length === 1) {
        toast.warning(`No WordPress URL set for ${skipped[0].toUpperCase()} — skipped`);
      } else {
        toast.success("WordPress lists refreshed");
      }
    } catch (e) {
      toast.error("WordPress refresh failed");
    } finally {
      setWpRefreshing(false);
    }
  };

  const testGhl = async (pub) => {    setTesting(pub);
    setGhlResult((r) => ({ ...(r || {}), [pub]: null }));
    try {
      const { data } = await api.post("/admin/ghl/test", { pub });
      setGhlResult((r) => ({ ...(r || {}), [pub]: data }));
      if (data.ok) toast.success(`${pub.toUpperCase()} connection OK`);
      else toast.error(`${pub.toUpperCase()} test failed`);
    } catch (e) {
      setGhlResult((r) => ({ ...(r || {}), [pub]: { ok: false, message: e.message } }));
      toast.error(`${pub.toUpperCase()} test failed`);
    } finally {
      setTesting(null);
    }
  };

  const addCat = () => {
    if (!newCat.trim()) return;
    set("categories", [...(s.categories || []), newCat.trim()]);
    setNewCat("");
  };
  const removeCat = (i) => {
    if ((s.categories[i] || "").toLowerCase() === "photographer") {
      toast.error("Photographer must remain pinned.");
      return;
    }
    set("categories", s.categories.filter((_, idx) => idx !== i));
  };
  const moveCat = (i, dir) => {
    const arr = [...s.categories];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    if (i === 0 || j === 0) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    set("categories", arr);
  };

  if (!s) return <div className="p-8 text-sm text-[#5c5852]">{loadError || "Loading…"}</div>;

  return (
    <div className="p-6 lg:p-10 max-w-4xl" data-testid="settings-page">
      <p className="btw-overline mb-1">Configuration</p>
      <h1 className="font-serif text-3xl mb-8">Settings</h1>

      <Section title="Brand assets">
        <Field label="DWH logo URL" testid="dwh-logo" value={s.dwh_logo_url || ""} onChange={(v) => set("dwh_logo_url", v)} />
        <Field label="DWT logo URL" testid="dwt-logo" value={s.dwt_logo_url || ""} onChange={(v) => set("dwt_logo_url", v)} />
        <Field label="DWH header image URL" testid="dwh-header" value={s.dwh_header_url || ""} onChange={(v) => set("dwh_header_url", v)} />
        <Field label="DWT header image URL" testid="dwt-header" value={s.dwt_header_url || ""} onChange={(v) => set("dwt_header_url", v)} />
      </Section>

      <Section title="Sender / email">
        <Field
          label="From email (used as the From: address on submission confirmations — must be a verified sender in GHL)"
          testid="from-email"
          value={s.from_email || ""}
          onChange={(v) => set("from_email", v)}
        />
        <Field label="DWH sender display name" testid="dwh-from-name" value={s.dwh_from_name || ""} onChange={(v) => set("dwh_from_name", v)} />
        <Field label="DWT sender display name" testid="dwt-from-name" value={s.dwt_from_name || ""} onChange={(v) => set("dwt_from_name", v)} />
      </Section>

      <Section title="Email (Resend)">
        <p className="text-xs text-[#5c5852] -mt-1">
          Sends all submission emails (magic links, invites, alerts) from your own Resend account.
          One API key covers both verified domains. If not configured — or if a send fails — emails fall back to the built-in sender.
        </p>
        <p className="text-xs text-[#5c5852] mb-3">Resend API credentials are managed in the backend deployment environment. This screen only controls sender addresses and tests delivery.</p>
        <Field label="Submission alert inbox" testid="alert-email" value={s.alert_email || ""} onChange={(v) => set("alert_email", v)} />
        {["dwh", "dwt"].map((pub) => {
          const st = s[`resend_${pub}_status`] || {};
          return (
            <div key={pub} className="border-t border-[#e2d3c0] pt-3 mt-3" data-testid={`resend-${pub}`}>
              <p className="text-xs uppercase tracking-wider text-[#5c5852] mb-2">{pub === "dwh" ? "Dancing With Her" : "Dancing With Them"}</p>
              <Field label={`${pub.toUpperCase()} From name`} testid={`resend-${pub}-from-name`} value={s[`${pub}_from_name`] || ""} onChange={(v) => set(`${pub}_from_name`, v)} />
              <Field label={`${pub.toUpperCase()} From address`} testid={`resend-${pub}-from-address`} value={s[`email_${pub}_from_address`] || ""} onChange={(v) => set(`email_${pub}_from_address`, v)} />
              <Field label={`${pub.toUpperCase()} Reply-To address`} testid={`resend-${pub}-reply-to`} value={s[`email_${pub}_reply_to`] || ""} onChange={(v) => set(`email_${pub}_reply_to`, v)} />
              {st.domain !== undefined && st.detail !== undefined && st.domain !== "" || st.detail ? (
                <p className={`text-xs mb-2 ${st.ok ? "text-[#5c6e53]" : "text-[#b35c5c]"} font-bold`} data-testid={`resend-${pub}-domain-status`}>
                  Domain check: {st.ok ? "✓" : "✗"} {st.domain || ""} — {st.detail}
                </p>
              ) : null}
              <div className="flex gap-2 items-center flex-wrap">
                <input className="px-3 py-2 border border-[#dcd3c6] bg-white text-sm w-64"
                  placeholder="Send test email to…" data-testid={`resend-${pub}-test-to`}
                  value={resendTestTo[pub]}
                  onChange={(e) => setResendTestTo({ ...resendTestTo, [pub]: e.target.value })} />
                <button className="btw-btn text-xs px-3 py-2" data-testid={`resend-${pub}-test-send`}
                  disabled={resendTesting === pub} onClick={() => testResend(pub)}>
                  {resendTesting === pub ? "Sending…" : "Send test email"}
                </button>
              </div>
            </div>
          );
        })}
        <p className="text-xs text-[#5c5852] mt-2">Domain check runs when you Save after changing the key or a From address.</p>
      </Section>

      <Section title="Publications — domains & legal links">
        <p className="text-xs text-[#5c5852] -mt-1">The default publication is detected from the domain; <code>?pub=dwh</code> / <code>?pub=dwt</code> always overrides.</p>
        <Field label="DWH hostname (e.g. submit.dancingwithher.com)" testid="dwh-hostname" value={s.dwh_hostname || ""} onChange={(v) => set("dwh_hostname", v)} />
        <Field label="DWT hostname (e.g. submit.dancingwiththem.com)" testid="dwt-hostname" value={s.dwt_hostname || ""} onChange={(v) => set("dwt_hostname", v)} />
        <Field label="DWH terms URL" testid="dwh-terms-url" value={s.dwh_terms_url || ""} onChange={(v) => set("dwh_terms_url", v)} />
        <Field label="DWH privacy URL" testid="dwh-privacy-url" value={s.dwh_privacy_url || ""} onChange={(v) => set("dwh_privacy_url", v)} />
        <Field label="DWT terms URL" testid="dwt-terms-url" value={s.dwt_terms_url || ""} onChange={(v) => set("dwt_terms_url", v)} />
        <Field label="DWT privacy URL" testid="dwt-privacy-url" value={s.dwt_privacy_url || ""} onChange={(v) => set("dwt_privacy_url", v)} />
      </Section>

      <Section title="Lead time targets">
        <p className="text-xs text-[#5c5852] -mt-1">Used on the Tracking page to set target publish dates and flag overdue submissions.</p>
        <Field label="Target days to publish — Priority" testid="target-days-priority" type="number" value={s.target_days_priority ?? 14} onChange={(v) => set("target_days_priority", Number(v))} />
        <Field label="Target days to publish — Standard" testid="target-days-standard" type="number" value={s.target_days_standard ?? 42} onChange={(v) => set("target_days_standard", Number(v))} />
      </Section>

      <Section title="Google Drive (Shared Drive)">
        <p className="text-xs text-[#5c5852] -mt-1">
          Folders and draft docs are created by our Google service account. DWH and DWT use
          separate Shared Drives (they can live in different Google accounts) — share each
          Shared Drive with the service account email (Content manager). One folder per
          submission is created inside the publication's Blogs folder.
        </p>
        <p className="text-xs text-[#5c5852] mb-3">Google service-account credentials are managed through GOOGLE_SERVICE_ACCOUNT_JSON in the backend deployment environment.</p>
        <Field label="DWH Shared Drive ID" testid="gdrive-dwh-drive" value={s.gdrive_dwh_drive_id || ""} onChange={(v) => set("gdrive_dwh_drive_id", v)} />
        <Field label="DWH Blogs folder ID" testid="gdrive-dwh-blogs" value={s.gdrive_dwh_blogs_id || ""} onChange={(v) => set("gdrive_dwh_blogs_id", v)} />
        <Field label="DWT Shared Drive ID" testid="gdrive-dwt-drive" value={s.gdrive_dwt_drive_id || ""} onChange={(v) => set("gdrive_dwt_drive_id", v)} />
        <Field label="DWT Blogs folder ID" testid="gdrive-dwt-blogs" value={s.gdrive_dwt_blogs_id || ""} onChange={(v) => set("gdrive_dwt_blogs_id", v)} />
        <div className="flex items-center gap-3 pt-2">
          <button type="button" onClick={testDrive} disabled={driveTesting} data-testid="test-drive" className="text-xs px-4 py-2 border border-[#c19872] text-[#876a4f] hover:bg-[#e2d3c0]/40">
            {driveTesting ? "Testing…" : "Test Drive access"}
          </button>
          {driveResult && (
            <div className="text-xs space-y-1" data-testid="drive-test-result">
              {["dwh", "dwt"].map((pub) => driveResult[pub] && (
                <p key={pub} className={driveResult[pub].ok ? "text-[#768c6a]" : "text-[#b35c5c]"}>
                  {pub.toUpperCase()}: {driveResult[pub].message}
                </p>
              ))}
              {driveResult.message && <p className="text-[#b35c5c]">{driveResult.message}</p>}
            </div>
          )}
        </div>
      </Section>

      <Section title="AI blog draft">
        <p className="text-xs text-[#5c5852] -mt-1">
          Prompts are used exactly as stored. The styled shoot prompt falls back to the Real
          Stories prompt plus a styled-shoot note until you fill it in.
        </p>
        <div>
          <label className="text-xs font-bold block mb-1">Real Stories prompt (all story types)</label>
          <textarea data-testid="real-stories-prompt" rows={8} className="w-full bg-transparent border border-[#dcd3c6] px-3 py-2 text-xs font-mono" value={s.real_stories_prompt || ""} onChange={(e) => set("real_stories_prompt", e.target.value)} />
        </div>
        <div>
          <label className="text-xs font-bold block mb-1">Styled shoot prompt</label>
          <textarea data-testid="styled-shoot-prompt" rows={5} className="w-full bg-transparent border border-[#dcd3c6] px-3 py-2 text-xs font-mono" placeholder="(empty = Real Stories prompt + styled-shoot note)" value={s.styled_shoot_prompt || ""} onChange={(e) => set("styled_shoot_prompt", e.target.value)} />
        </div>
        <div>
          <label className="text-xs font-bold block mb-1">Banner product list (one per line)</label>
          <textarea data-testid="banner-products" rows={4} className="w-full bg-transparent border border-[#dcd3c6] px-3 py-2 text-sm" value={(s.banner_products || []).join("\n")} onChange={(e) => set("banner_products", e.target.value.split("\n").map((x) => x.trim()).filter(Boolean))} />
        </div>
        <div>
          <label className="text-xs font-bold block mb-1">Banned words (comma separated)</label>
          <textarea data-testid="banned-words" rows={2} className="w-full bg-transparent border border-[#dcd3c6] px-3 py-2 text-sm" value={(s.banned_words || []).join(", ")} onChange={(e) => set("banned_words", e.target.value.split(/[,\n]/).map((x) => x.trim()).filter(Boolean))} />
        </div>
      </Section>

      <Section title="Instagram handles (social vendor list)">
        <Field label="DWH Instagram handle" testid="dwh-ig-handle" value={s.dwh_ig_handle || ""} onChange={(v) => set("dwh_ig_handle", v.replace(/^@/, ""))} />
        <Field label="DWT Instagram handle" testid="dwt-ig-handle" value={s.dwt_ig_handle || ""} onChange={(v) => set("dwt_ig_handle", v.replace(/^@/, ""))} />
      </Section>

      <Section title="Category display names (website vendor list)">
        <p className="text-xs text-[#5c5852] -mt-1">
          One mapping per line: <code>Stored category = Shown as</code> (e.g. <code>Suit / Menswear = Suit</code>). Unmapped categories display as stored.
        </p>
        <textarea
          data-testid="category-display-map"
          className="w-full bg-transparent border border-[#dcd3c6] px-3 py-2 text-sm font-mono"
          rows={4}
          placeholder={"Suit / Menswear = Suit"}
          value={Object.entries(s.category_display || {}).map(([k, v]) => `${k} = ${v}`).join("\n")}
          onChange={(e) => {
            const map = {};
            e.target.value.split("\n").forEach((line) => {
              const idx = line.indexOf("=");
              if (idx > 0) {
                const k = line.slice(0, idx).trim();
                const v = line.slice(idx + 1).trim();
                if (k) map[k] = v;
              }
            });
            set("category_display", map);
          }}
        />
      </Section>

      <Section title="WordPress (read-only)">
        <p className="text-xs text-[#5c5852] -mt-1">
          Categories and tags are read daily from each site's REST API and stored here.
        </p>
        <Field label="DWH WordPress site URL" testid="wp-dwh-url" value={s.wp_dwh_url || ""} onChange={(v) => set("wp_dwh_url", v)} />
        <Field label="DWT WordPress site URL" testid="wp-dwt-url" value={s.wp_dwt_url || ""} onChange={(v) => set("wp_dwt_url", v)} />
        <div className="text-xs text-[#5c5852] space-y-1" data-testid="wp-cache-info">
          {["dwh", "dwt"].map((pub) => {
            const c = (s.wp_cache || {})[pub];
            return (
              <p key={pub} data-testid={`wp-cache-${pub}`}>
                <strong className="uppercase">{pub}</strong>:{" "}
                {c
                  ? c.error
                    ? <span className="text-[#b35c5c]">error: {c.error}</span>
                    : `${c.categories_count ?? 0} categories · ${c.tags_count ?? 0} tags · refreshed ${c.refreshed_at ? new Date(c.refreshed_at).toLocaleString() : "—"}`
                  : "never refreshed"}
              </p>
            );
          })}
        </div>
        <div className="flex items-center gap-3 pt-2">
          <button type="button" onClick={refreshWp} disabled={wpRefreshing} data-testid="wp-refresh" className="text-xs px-4 py-2 border border-[#c19872] text-[#876a4f] hover:bg-[#e2d3c0]/40">
            {wpRefreshing ? "Refreshing…" : "Refresh now"}
          </button>
          {wpResult && <span className="text-xs text-[#768c6a]" data-testid="wp-refresh-result">Done</span>}
        </div>
      </Section>

      <Section title="GoHighLevel — Dancing With Her sub-account">
        <p className="text-xs text-[#5c5852] -mt-1 mb-3">DWH credentials and Location ID are managed through GHL_DWH_API_KEY and GHL_DWH_LOCATION_ID in the backend deployment environment.</p>
        <p className="text-xs text-[#5c5852]">Custom field boxes accept the field's Unique Key as shown in GHL (e.g. <code>{"{{ contact.submission_admin_link }}"}</code>) — the ID is looked up on save — or a raw field ID.</p>
        <CfField pub="dwh" f="admin_link" label="Custom field — Submission admin link" testid="ghl-dwh-cf-admin" />
        <CfField pub="dwh" f="live_url" label="Custom field — Live feature URL" testid="ghl-dwh-cf-live" />
        <CfField pub="dwh" f="lovers_names" label="Custom field — Lovers' names" testid="ghl-dwh-cf-names" />
        <CfField pub="dwh" f="story_type" label="Custom field — Story type" testid="ghl-dwh-cf-story" />
        <CfField pub="dwh" f="all_features" label="Custom field — All features" testid="ghl-dwh-cf-all" />
        <CfField pub="dwh" f="referral_invite_sent_at" label="Custom field — Referral invite sent at" testid="ghl-dwh-cf-invite" />
        <CfField pub="dwh" f="referral_accepted_at" label="Custom field — Referral accepted at" testid="ghl-dwh-cf-accepted" />
        <CfField pub="dwh" f="referring_pro" label="Custom field — Referring pro" testid="ghl-dwh-cf-refpro" />
        <CfField pub="dwh" f="blog_live_date" label="Custom field — Blog Live Date (scheduled publish)" testid="ghl-dwh-cf-blog-date" />
        <CfField pub="dwh" f="blog_live_url" label="Custom field — Blog Live URL (scheduled publish)" testid="ghl-dwh-cf-blog-url" />
        <div className="flex items-center gap-3 pt-2">
          <button type="button" onClick={() => testGhl("dwh")} disabled={!!testing} data-testid="test-ghl-dwh" className="text-xs px-4 py-2 border border-[#c19872] text-[#876a4f] hover:bg-[#e2d3c0]/40">
            {testing === "dwh" ? "Testing…" : "Test connection"}
          </button>
          {ghlResult?.dwh && (
            <div className={`text-xs inline-flex items-center gap-2 ${ghlResult.dwh.ok ? "text-[#768c6a]" : "text-[#b35c5c]"}`} data-testid="ghl-test-result-dwh">
              {ghlResult.dwh.ok ? <CheckCircle2 size={14} /> : <AlertCircle size={14} />}
              <span>{ghlResult.dwh.message || (ghlResult.dwh.ok ? "Connection OK" : "Failed")}</span>
            </div>
          )}
        </div>
      </Section>

      <Section title="GoHighLevel — Dancing With Them sub-account">
        <p className="text-xs text-[#5c5852] -mt-1 mb-3">DWT credentials and Location ID are managed through GHL_DWT_API_KEY and GHL_DWT_LOCATION_ID in the backend deployment environment.</p>
        <p className="text-xs text-[#5c5852]">Custom field boxes accept the field's Unique Key as shown in GHL (e.g. <code>{"{{ contact.submission_admin_link }}"}</code>) — the ID is looked up on save — or a raw field ID.</p>
        <CfField pub="dwt" f="admin_link" label="Custom field — Submission admin link" testid="ghl-dwt-cf-admin" />
        <CfField pub="dwt" f="live_url" label="Custom field — Live feature URL" testid="ghl-dwt-cf-live" />
        <CfField pub="dwt" f="lovers_names" label="Custom field — Lovers' names" testid="ghl-dwt-cf-names" />
        <CfField pub="dwt" f="story_type" label="Custom field — Story type" testid="ghl-dwt-cf-story" />
        <CfField pub="dwt" f="all_features" label="Custom field — All features" testid="ghl-dwt-cf-all" />
        <CfField pub="dwt" f="referral_invite_sent_at" label="Custom field — Referral invite sent at" testid="ghl-dwt-cf-invite" />
        <CfField pub="dwt" f="referral_accepted_at" label="Custom field — Referral accepted at" testid="ghl-dwt-cf-accepted" />
        <CfField pub="dwt" f="referring_pro" label="Custom field — Referring pro" testid="ghl-dwt-cf-refpro" />
        <CfField pub="dwt" f="blog_live_date" label="Custom field — Blog Live Date (scheduled publish)" testid="ghl-dwt-cf-blog-date" />
        <CfField pub="dwt" f="blog_live_url" label="Custom field — Blog Live URL (scheduled publish)" testid="ghl-dwt-cf-blog-url" />
        <div className="flex items-center gap-3 pt-2">
          <button type="button" onClick={() => testGhl("dwt")} disabled={!!testing} data-testid="test-ghl-dwt" className="text-xs px-4 py-2 border border-[#c19872] text-[#876a4f] hover:bg-[#e2d3c0]/40">
            {testing === "dwt" ? "Testing…" : "Test connection"}
          </button>
          {ghlResult?.dwt && (
            <div className={`text-xs inline-flex items-center gap-2 ${ghlResult.dwt.ok ? "text-[#768c6a]" : "text-[#b35c5c]"}`} data-testid="ghl-test-result-dwt">
              {ghlResult.dwt.ok ? <CheckCircle2 size={14} /> : <AlertCircle size={14} />}
              <span>{ghlResult.dwt.message || (ghlResult.dwt.ok ? "Connection OK" : "Failed")}</span>
            </div>
          )}
        </div>
      </Section>

      <Section title="Vendor categories">
        <p className="text-xs text-[#5c5852] mb-3">Photographer is always pinned first.</p>
        <div className="space-y-2 mb-4">
          {(s.categories || []).map((c, i) => (
            <div key={i} className="flex items-center justify-between border border-[#dcd3c6] bg-white px-3 py-2" data-testid={`cat-${i}`}>
              <div className="flex items-center gap-2">
                <GripVertical size={14} className="text-[#5c5852]" />
                <span className={i === 0 ? "font-medium" : ""}>{c}</span>
                {i === 0 && <span className="text-[10px] uppercase tracking-wider text-[#876a4f]">pinned</span>}
              </div>
              <div className="flex gap-2">
                {i > 1 && <button onClick={() => moveCat(i, -1)} className="text-xs text-[#5c5852] hover:underline">↑</button>}
                {i > 0 && i < s.categories.length - 1 && <button onClick={() => moveCat(i, 1)} className="text-xs text-[#5c5852] hover:underline">↓</button>}
                {i !== 0 && (
                  <button onClick={() => removeCat(i)} className="text-[#b35c5c]" data-testid={`del-cat-${i}`}>
                    <X size={14} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <input
            data-testid="new-category"
            value={newCat}
            onChange={(e) => setNewCat(e.target.value)}
            placeholder="New category name"
            className="flex-1 px-3 py-2 border border-[#dcd3c6] bg-white text-sm"
          />
          <button onClick={addCat} data-testid="add-category" className="px-3 py-2 border border-[#c19872] text-[#876a4f] text-sm hover:bg-[#e2d3c0]/40 inline-flex items-center gap-1">
            <Plus size={14} /> Add
          </button>
        </div>
      </Section>

      <Section title="Story questions — edit and mark which questions must always be asked">
        <p className="text-xs text-[#5c5852] mb-4">
          Wedding / Elopement / Family / Proposal / Engagement / Love Story: lovers are asked 7 questions; any flagged "Always include" are guaranteed and the rest are filled randomly. Styled Shoot: ALL questions are asked. Save the page to apply.
        </p>
        <StoryEditorTabs
          storyLib={storyLib}
          updateQuestion={updateQuestion}
          addQuestion={addQuestion}
          removeQuestion={removeQuestion}
        />
      </Section>

      <RetentionCard />

      <div className="flex justify-end mt-8 sticky bottom-4">
        <button onClick={save} disabled={saving} data-testid="settings-save" className="btw-btn shadow-sm">
          {saving ? "Saving…" : "Save settings"}
        </button>
      </div>
    </div>
  );
}

const RetentionCard = () => {
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/retention");
      setReport(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };
  useEffect(() => { load(); }, []);

  const toggle = async () => {
    const next = !report.enabled;
    setBusy(true);
    try {
      await api.patch("/admin/settings", { retention_enabled: next });
      toast.success(next
        ? "Retention switched ON — the nightly job will now delete candidates"
        : "Retention switched OFF — report-only mode");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const applyNow = async () => {
    if (!window.confirm(`Delete ${report.count} submission(s) and their files now? This cannot be undone.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/retention/apply");
      toast.success(`Deleted ${data.count} submission(s)`);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  if (!report) return null;
  return (
    <section className="mb-10" data-testid="retention-section">
      <h2 className="btw-overline mb-4">Data retention</h2>
      <div className="bg-white border border-[#dcd3c6] p-5 space-y-4">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <p className="text-sm text-black max-w-xl">
            Declined or abandoned submissions (with their uploaded files) are deleted
            {" "}{report.months} months after being declined or last edited. Published
            features, vendors, admins and GHL contacts are never touched.
          </p>
          <span className={`text-xs px-3 py-1 rounded-full ${report.enabled ? "bg-black text-white" : "bg-white border border-[#c19872] text-black"}`} data-testid="retention-mode">
            {report.enabled ? "Live" : "Report-only"}
          </span>
        </div>

        <div className="text-sm">
          {report.count === 0 ? (
            <p className="text-[#5c5852]" data-testid="retention-empty">Nothing old enough to delete right now.</p>
          ) : (
            <>
              <p className="font-bold mb-2" data-testid="retention-count">
                {report.count} submission(s) would be deleted{report.enabled ? " by the nightly job" : " if switched on"}:
              </p>
              <ul className="space-y-1" data-testid="retention-candidates">
                {report.candidates.map((c) => (
                  <li key={c.id} className="text-xs text-black flex flex-wrap gap-x-2" data-testid={`retention-candidate-${c.id}`}>
                    <span className="font-medium">{c.event_name}</span>
                    <span className="text-[#5c5852]">
                      {c.publication} · {c.reason} · {new Date(c.since).toLocaleDateString()}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        <div className="flex items-center gap-3 flex-wrap pt-2 border-t border-[#dcd3c6]">
          <button onClick={toggle} disabled={busy} data-testid="retention-toggle"
                  className="btw-btn-outline text-xs">
            {report.enabled ? "Switch off (report-only)" : "Switch on (start deleting)"}
          </button>
          {report.enabled && report.count > 0 && (
            <button onClick={applyNow} disabled={busy} data-testid="retention-apply"
                    className="text-xs px-3 py-2 bg-[#b35c5c] text-white border border-[#b35c5c] hover:opacity-85">
              Delete {report.count} now
            </button>
          )}
        </div>

        {(report.removal_log || []).length > 0 && (
          <div className="pt-2 border-t border-[#dcd3c6]">
            <p className="text-xs font-bold mb-2">Removal log</p>
            <ul className="space-y-1" data-testid="removal-log">
              {report.removal_log.slice(0, 10).map((r) => (
                <li key={r.id} className="text-xs text-[#5c5852]">
                  {new Date(r.at).toLocaleString()} — {r.event_name} ({r.publication}) · {r.reason} · by {r.by}
                  {r.drive_folder_deleted ? " · files deleted" : ""}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
};

const Section = ({ title, children }) => (
  <section className="mb-10">
    <h2 className="btw-overline mb-4">{title}</h2>
    <div className="bg-white border border-[#dcd3c6] p-5 space-y-4">{children}</div>
  </section>
);

const Field = ({ label, value, onChange, type = "text", testid }) => (
  <div>
    <label className="btw-overline block mb-1">{label}</label>
    <input
      data-testid={testid}
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full border-b border-[#dcd3c6] focus:border-[#c19872] outline-none py-2 text-sm bg-transparent"
    />
  </div>
);

const StoryEditorTabs = ({ storyLib, updateQuestion, addQuestion, removeQuestion }) => {
  const [active, setActive] = useState(STORY_KEYS[0].key);
  const rows = storyLib[active] || [];
  const alwaysCount = rows.filter((r) => r.always).length;
  return (
    <div data-testid="story-editor-tabs">
      {/* Tab strip */}
      <div className="flex flex-wrap gap-1 border-b border-[#dcd3c6] mb-0">
        {STORY_KEYS.map(({ key, label }) => {
          const count = (storyLib[key] || []).length;
          const isActive = active === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => setActive(key)}
              data-testid={`story-tab-${key}`}
              className={`relative px-4 py-2.5 text-sm font-semibold transition-colors -mb-px ${
                isActive
                  ? "bg-white border border-[#dcd3c6] border-b-white text-[#2a2825]"
                  : "text-[#5c5852] hover:text-[#2a2825] border border-transparent"
              }`}
            >
              {label}
              <span className={`ml-2 text-[10px] font-normal px-1.5 py-0.5 rounded-full ${isActive ? "bg-[#e2d3c0]/60 text-[#5c5852]" : "bg-[#dcd3c6]/40 text-[#5c5852]"}`}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Active tab content */}
      <div className="bg-white border border-[#dcd3c6] border-t-0 p-5" data-testid={`story-cat-${active}`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-bold text-[#2a2825]">
            {STORY_KEYS.find((k) => k.key === active)?.label}
          </h3>
          <span className="text-[11px] text-[#5c5852]">
            {rows.length} total · {alwaysCount} always-included
          </span>
        </div>

        <div className="space-y-2">
          {rows.map((row, i) => (
            <div
              key={i}
              className="flex items-start gap-3 border border-[#dcd3c6] bg-[#f5f3ed]/40 p-3 rounded-sm"
              data-testid={`story-q-row-${active}-${i}`}
            >
              <span className="text-xs text-[#5c5852] font-bold pt-2 min-w-[20px]">
                {i + 1}.
              </span>
              <textarea
                rows={2}
                data-testid={`story-q-input-${active}-${i}`}
                value={row.q}
                onChange={(e) => updateQuestion(active, i, { q: e.target.value })}
                placeholder="Question text…"
                className="flex-1 bg-white border border-[#dcd3c6] focus:border-[#c19872] outline-none px-3 py-2 text-sm font-sans resize-none"
              />
              <label className="text-xs text-[#5c5852] inline-flex items-center gap-1 cursor-pointer whitespace-nowrap pt-2">
                <input
                  type="checkbox"
                  data-testid={`story-always-${active}-${i}`}
                  checked={!!row.always}
                  onChange={(e) => updateQuestion(active, i, { always: e.target.checked })}
                  className="h-3.5 w-3.5 accent-[#c19872]"
                />
                Always
              </label>
              <button
                type="button"
                onClick={() => removeQuestion(active, i)}
                data-testid={`story-q-del-${active}-${i}`}
                className="text-[#5c5852] hover:text-[#b35c5c] pt-2"
                title="Delete question"
              >
                <X size={14} />
              </button>
            </div>
          ))}
          {rows.length === 0 && (
            <p className="text-xs text-[#5c5852] py-3 italic">No questions yet — add your first below.</p>
          )}
        </div>

        <button
          type="button"
          onClick={() => addQuestion(active)}
          data-testid={`story-q-add-${active}`}
          className="mt-4 text-xs inline-flex items-center gap-1 px-3 py-2 border border-[#c19872] text-[#876a4f] hover:bg-[#e2d3c0]/40 font-semibold"
        >
          <Plus size={12} /> Add question
        </button>
      </div>
    </div>
  );
};

